import { useQuery } from '@tanstack/react-query';
import FormControlledDropdown from '../../../../components/inputs/FormInputs/FormControlledDropdown';
import { useCompanyContext } from '../../../../context/CompanyContext';
import {
  fecthSCVendors,
  fetchSubcontracts,
} from '../../../../services/accounting/SubcontractsService';
import { useFormContext, useWatch } from 'react-hook-form';
import { SubcontractCOFormFields } from '../../../../Interfaces/Accounting/ACForms.interfaces';
import SLChangeOrderTableContainer from './SLChangeOrderTableContainer';
import ErrorToast, { showToast } from '../../../../components/messages/ErrorAlert';
import { useRef } from 'react';
import { Toast } from 'primereact/toast';

const SLChangeOrderInfo = () => {
  const { selectedCompany } = useCompanyContext();
  const toast = useRef<Toast>(null);
  const sl = useWatch<SubcontractCOFormFields>({ name: 'sl' }) as string;
  const { setValue } = useFormContext<SubcontractCOFormFields>();

  const getSubcontracts = useQuery({
    queryKey: ['getSubcontracts', selectedCompany],
    queryFn: ({ signal }) =>
      fetchSubcontracts(selectedCompany!.id, undefined, [0], signal),
    onSuccess: () => {
      const subcontract = localStorage.getItem('temp-sl-co-subcontract');
      const company = localStorage.getItem('temp-sl-co-company');

      if (subcontract && selectedCompany!.id === company) {
        setValue('sl', subcontract);

        showToast(
          'info',
          toast,
          'Subcontract Change Order',
          'Data was restored from temporary backup.',
          5000
        );
      }
      localStorage.removeItem('temp-sl-co-subcontract');
    },
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany,
  });

  const selectedSubcontract: {
    SL: string;
    job: string;
    jobDescription: string;
    retainage: number;
  } | null = getSubcontracts.data?.find(
    (subcontract: {
      SL: string;
      job: string;
      jobDescription: string;
      retainage: number;
    }) => subcontract.SL === sl
  );

  const vendorRequest = useQuery({
    queryKey: [
      'getSubcontractVendor',
      selectedCompany,
      sl,
      selectedSubcontract?.job,
    ],
    queryFn: ({ signal }) => {
      return fecthSCVendors(
        {
          companyID: selectedCompany!.id,
          job: selectedSubcontract?.job ?? '',
          subcontract: sl ?? '',
        },
        signal
      );
    },
    refetchOnWindowFocus: false,
    enabled: !!(selectedCompany?.id && selectedSubcontract?.job && sl),
  });

  const vendor = (vendorRequest.data || [])[0];

  return (
    <div className="grid w-full">
      <ErrorToast toastRef={toast} />
      <div className="col-3 ml-3">
        <label htmlFor="job" className="text-standard block mb-2">
          Subcontract #
        </label>
        <FormControlledDropdown
          formID="sl"
          options={getSubcontracts.data}
          labelField="SL"
          valueField="SL"
          placeholder={
            getSubcontracts.isError
              ? 'Failed to load Subcontracts!'
              : 'Select Subcontract'
          }
          isDisabled={getSubcontracts.isError}
          isLoading={getSubcontracts.isFetching}
          clearable={true}
          filter={true}
          resetFilterOnHide={true}
          rules={{
            required: {
              value: true,
              message: 'Subcontract # is a required field',
            },
          }}
          additionalChange={(value) => {
            setValue('items', []);
            if (value) {
              localStorage.setItem('temp-sl-co-subcontract', value as string);
              localStorage.setItem('temp-sl-co-company', selectedCompany!.id);
            } else {
              localStorage.removeItem('temp-sl-co-subcontract');
            }
            localStorage.removeItem('temp-sl-co-items');
            localStorage.removeItem('temp-sl-co-items-original');
          }}
          className="printHide"
        />
        <span className="printMedia printShow">{sl}</span>
      </div>
      {vendor && selectedSubcontract && (
        <div className="col-3 flex flex-column">
          <label htmlFor="job" className="text-standard block mb-2">
            Job
          </label>
          <div className="text-standard my-auto">
            {selectedSubcontract.job} - {selectedSubcontract.jobDescription}
          </div>
        </div>
      )}
      {vendor && (
        <div className="col-3 flex flex-column">
          <label htmlFor="job" className="text-standard block mb-2">
            Vendor
          </label>
          <div className="text-standard my-auto">
            {vendor.Vendor} - {vendor.Name}
          </div>
        </div>
      )}
      <div className="col-12">
        <SLChangeOrderTableContainer
          subcontract={selectedSubcontract ?? { SL: '', job: '', retainage: 0 }}
        />
      </div>
    </div>
  );
};

export default SLChangeOrderInfo;
